import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, Users, UserCog, Trophy, Sparkles, ChevronRight } from 'lucide-react';
import { ROUTES } from '@/constants/routes';

interface FirstRunChecklistProps {
  profileComplete: boolean;
  hasGroup: boolean;
  hasPlayed: boolean;
}

interface Step {
  icon: React.ElementType;
  title: string;
  body: string;
  cta: string;
  to: string;
  done: boolean;
}

export const FirstRunChecklist: React.FC<FirstRunChecklistProps> = ({
  profileComplete,
  hasGroup,
  hasPlayed,
}) => {
  const navigate = useNavigate();

  if (profileComplete && hasGroup && hasPlayed) return null;

  const steps: Step[] = [
    {
      icon: UserCog,
      title: 'Complete your profile',
      body: 'Level, location and playing style power fair pairings.',
      cta: 'Edit profile',
      to: ROUTES.EDIT_PROFILE,
      done: profileComplete,
    },
    {
      icon: Users,
      title: 'Join or create a group',
      body: 'Groups unlock polls, tournaments, leagues, ladders and rankings.',
      cta: hasGroup ? 'Your groups' : 'Find a group',
      to: hasGroup ? ROUTES.YOUR_GROUPS : ROUTES.GROUPS,
      done: hasGroup,
    },
    {
      icon: Trophy,
      title: 'Play and capture a score',
      body: 'Scores feed your rating, ladder position and match history.',
      cta: 'Create a match',
      to: ROUTES.CREATE_MATCH,
      done: hasPlayed,
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;

  return (
    <div className="mx-4 mb-6 rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <Sparkles className="h-4 w-4 text-primary" />
          Get set up
        </h2>
        <span className="text-xs font-medium text-muted-foreground">{doneCount}/3 done</span>
      </div>

      <ul className="mt-3 space-y-2">
        {steps.map((step) => (
          <li
            key={step.title}
            className="flex items-start gap-3 rounded-lg border border-border/60 bg-background/40 p-3"
          >
            {step.done ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            ) : (
              <Circle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            )}
            <div className="min-w-0 flex-1">
              <p
                className={`text-sm font-medium ${
                  step.done ? 'text-muted-foreground line-through' : 'text-foreground'
                }`}
              >
                {step.title}
              </p>
              {!step.done && (
                <>
                  <p className="mt-0.5 text-xs text-muted-foreground">{step.body}</p>
                  <button
                    type="button"
                    onClick={() => navigate(step.to)}
                    className="mt-1.5 flex min-h-[44px] items-center gap-0.5 text-sm font-medium text-primary"
                  >
                    {step.cta} <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => navigate(ROUTES.FEATURE_GUIDE)}
        className="mt-3 flex min-h-[44px] items-center gap-0.5 text-sm font-medium text-primary"
      >
        See everything you can do in LuvALL <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};

export default FirstRunChecklist;
